/* eslint-disable no-console, camelcase */
import Vue from 'vue'
import debounce from '~/utils/debounce'
import appendUrlParam from '~/utils/appendUrlParam'

/**
 * @typedef {{
 *   item_id: string,
 *   line_item_id?: number,
 *   product_id?: string | number,
 *   variant_id: string | number,
 *   device?: {
 *     id: number,
 *     brand?: string,
 *     code?: string,
 *     name?: string,
 *     position?: number,
 *   },
 *   case_device_id?: string | number,
 *   case_device?: string,
 *   case_type?: {
 *     id: number,
 *     code?: string,
 *     name?: string,
 *     case_image?: string,
 *     details?: any,
 *     devices?: Array<any>,
 *   },
 *   product_type_code?: string,
 *   name: string,
 *   slug?: string,
 *   image_url: string,
 *   customizations?: Record<string, string>,
 *   raw_customizations?: Record<string, string>,
 *   server_customizations?: Record<string, string>,
 *   engine_information?: string,
 *   quantity: number,
 *   price: number,
 *   old_price?: number,
 *   added_by_suggestion?: boolean,
 *   is_gift?: boolean,
 *   is_server_sync: boolean,
 *   is_out_of_stock?: boolean,
 *   eligible_gifts?: Array<{
 *     variant_id: string | number,
 *     name: string,
 *     image?: string,
 *     in_stock?: boolean,
 *   }>,
 *   gift_for_item_id?: string,
 * }} CartItem
 */

/**
 * @typedef {{
 *   items: CartItem[],
 *   userId: string | null,
 *   orderNumber: string | null,
 *   guestToken: string | null,
 *   zipCode: string | null,
 *   isOpen: boolean,
 *   isServerSyncing: boolean,
 * }} CartState
 */

/**
 * @param {Partial<CartItem>} partCartItem
 * @param {Record<string, string>} customizations
 */
const generateCartItemId = (partCartItem, customizations) => {
  let variantId = partCartItem.variant_id || ''
  if (partCartItem.is_gift) {
    // Gifts are creating a line item each unit
    return `${variantId}_${Date.now()}`
  }
  if (partCartItem.case_device_id) {
    variantId += `.${partCartItem.case_device_id}`
  } else {
    if (partCartItem.device?.id) variantId += `_${partCartItem.device.id}`
    if (partCartItem.case_type?.id) variantId += `_${partCartItem.case_type.id}`
  }
  if (!customizations) {
    customizations = partCartItem.customizations || {}
  }
  const customizationKeys = Object.keys(customizations).sort()
  const customizationHash = customizationKeys
    .map((key) => {
      const strValue = `${customizations[key]}`
      if (strValue.startsWith('https://')) {
        try {
          const url = new URL(strValue)
          const pathname = url.pathname
          const filename = pathname.split('/').pop()
          const imgHash = filename.split('.')[0]
          return imgHash
        } catch {
          //
        }
      }
      return btoa(`${strValue}${key}`)
        .replace(/[^a-zA-Z0-9]/g, '')
        .substring(0, 32)
    })
    .join('|')
  if (!customizationHash) {
    return `${variantId}`
  }
  return `${variantId}_${customizationHash}`
}

/**
 * @returns {CartState}
 */
export const state = () => ({
  isEnabled: false,
  items: [],
  userId: null,
  orderNumber: null,
  guestToken: null,
  zipCode: null,
  isOpen: false,
  isServerSyncing: false,
})

export const getters = {
  /**
   * @param {CartState} state
   * @returns {CartItem[]}
   */
  itemsNeedingSync: (state) => {
    return state.items.filter((item) => {
      return !item.line_item_id || !item.is_server_sync
    })
  },

  /**
   * @param {CartState} state
   * @param {*} getters
   */
  isServerSync: (state, getters) => {
    return getters.itemsNeedingSync.length === 0
  },

  /**
   * @param {CartState} state
   * @returns {CartItem[]}
   */
  activeItems: (state) => {
    return state.items.filter((item) => {
      return item?.quantity && !item.is_out_of_stock
    })
  },

  /**
   * @param {CartState} state
   * @param {*} getters
   * @returns {number}
   */
  giftItemsCount: (state, getters) => {
    return getters.activeItems.reduce((acc, /** @type {CartItem} */ item) => {
      if (!item.is_gift) return acc
      return acc + item.quantity
    }, 0)
  },

  /**
   * @param {CartState} state
   * @param {*} getters
   * @returns {number}
   */
  expectedGiftsCount: (state, getters) => {
    return getters.activeItems.reduce((acc, /** @type {CartItem} */ item) => {
      if (!item.eligible_gifts?.length) return acc
      return acc + item.quantity
    }, 0)
  },

  /**
   * @param {CartState} state
   * @param {*} getters
   * @returns {boolean}
   */
  isGiftsMatching: (state, getters) => {
    return getters.expectedGiftsCount === getters.giftItemsCount
  },

  /**
   * @param {CartState} state
   * @param {*} getters
   * @returns {number}
   */
  itemsCount: (state, getters) => {
    return getters.activeItems.reduce((acc, /** @type CartItem */ item) => {
      if (item.is_gift && !getters.isGiftsMatching) return acc
      return acc + item.quantity
    }, 0)
  },

  /**
   * @param {CartState} state
   * @param {*} getters
   * @returns {number}
   */
  subtotal: (state, getters) => {
    return getters.activeItems.reduce((acc, /** @type CartItem */ item) => {
      if (item.is_gift) return acc
      const { quantity, price } = item
      if (!price) return acc
      return acc + quantity * price
    }, 0)
  },

  /**
   * @param {CartState} state
   * @param {*} getters
   * @returns {CartItem[]}
   */
  visibleItems: (state, getters) => {
    return state.items.filter((item) => {
      if (!item?.quantity) return false
      if (item.is_gift && !getters.isGiftsMatching) return false
      return true
    })
  },
}

/**
 * @param {Record<string, any> & CartItem} productOrItem
 */
const normalizeItem = (productOrItem) => {
  /** @type {Partial<CartItem>} */
  const partCartItem = {
    product_id: productOrItem.id,
    image_url: productOrItem.image,
    name: productOrItem.title,
    quantity: 1,
    is_server_sync: false,
  }
  /** @type {Array<keyof CartItem>} */
  const fields = [
    'added_by_suggestion',
    'case_device',
    'case_device_id',
    'case_type',
    'customizations',
    'device',
    'engine_information',
    'gift_for_item_id',
    'image_url',
    'is_gift',
    'is_server_sync',
    'item_id',
    'line_item_id',
    'name',
    'old_price',
    'price',
    'product_id',
    'product_type_code',
    'quantity',
    'raw_customizations',
    'slug',
    'variant_id',
  ]
  Object.keys(productOrItem).forEach((key) => {
    const value = productOrItem[key]
    if (!value && partCartItem[key]) return
    if (!fields.includes(key)) {
      if (key.startsWith('customizations[')) {
        const customizationId = key.substring(
          'customizations['.length,
          key.length - 1
        )
        if (customizationId) {
          if (!partCartItem.customizations) {
            partCartItem.customizations = {}
          }
          partCartItem.customizations[customizationId] = value
        }
      }
      if (key === 'gift' && typeof value === 'boolean') {
        partCartItem.is_gift = value
      }
      if (key === 'image' && typeof value === 'string') {
        partCartItem.image_url = value
      }
      return
    }
    partCartItem[key] = value
  })
  if (!partCartItem.variant_id) {
    throw new Error('Missing variant ID')
  }
  if (!partCartItem.image_url) {
    throw new Error('Missing item image URL')
  }
  if (!partCartItem.device?.id && productOrItem.device_id) {
    partCartItem.device = { id: Number(productOrItem.device_id) }
  }
  partCartItem.quantity = Number(partCartItem.quantity) || 1
  partCartItem.price = Number(partCartItem.price) || 0
  if (!partCartItem.price && !partCartItem.is_gift) {
    throw new Error('Missing item price')
  }
  if (partCartItem.old_price) {
    partCartItem.old_price = Number(partCartItem.old_price)
  }
  const engineInfo = partCartItem.engine_information
  if (engineInfo && typeof engineInfo === 'string') {
    try {
      partCartItem.engine_information = JSON.parse(engineInfo)
    } catch {
      delete partCartItem.engine_information
    }
  }
  if (!partCartItem.item_id) {
    const customizationsToKey =
      partCartItem.raw_customizations || partCartItem.customizations
    partCartItem.item_id = generateCartItemId(partCartItem, customizationsToKey)
  }
  /** @type {CartItem} */
  const cartItem = partCartItem
  return cartItem
}

/**
 * @param {CartState['items']} items
 * @param {number} itemIndex
 */
const dropCartItem = (items, itemIndex) => {
  const item = items[itemIndex]
  items.splice(itemIndex, 1)
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].gift_for_item_id === item.item_id) {
      dropCartItem(items, i)
    }
  }
}

/**
 * @param {CartItem} cartItem
 * @param {CartItem['variant_id']} giftVariantId
 */
const findEligibleGift = (cartItem, giftVariantId) => {
  return cartItem.eligible_gifts?.find((eligibleGift) => {
    return Number(eligibleGift.variant_id) === Number(giftVariantId)
  })
}

export const mutations = {
  /**
   * @param {CartState} state
   */
  enable(state) {
    state.isEnabled = true
    globalThis.CART_MODE = 'minicart'
  },

  /**
   * @param {CartState} state
   */
  toggle(state) {
    state.isOpen = !state.isOpen
  },

  /**
   * @param {CartState} state
   * @param {CartItem} item
   */
  ADD_ITEM(state, item) {
    if (!item.item_id) {
      throw new Error('Missing item ID')
    }
    if (!item.is_gift) {
      // Prefer no duplicated line items, excepting for gifts
      state.items = state.items.filter((_item) => _item?.item_id)
      const sameItem = state.items.find((_item) => {
        return _item.item_id === item.item_id && _item.quantity
      })
      if (sameItem) {
        sameItem.quantity += item.quantity
        return
      }
    }
    state.items.push(item)
  },

  /**
   * @param {CartState} state
   */
  RESET_CART(state) {
    state.userId = null
    state.orderNumber = null
    state.guestToken = null
    state.items = []
  },

  /**
   * @param {CartState} state
   * @param {string | null} userId
   */
  SET_USER_ID(state, userId) {
    state.userId = userId
  },

  /**
   * @param {CartState} state
   * @param {string | null} orderNumber
   */
  SET_ORDER_NUMBER(state, orderNumber) {
    state.orderNumber = orderNumber
  },

  /**
   * @param {CartState} state
   * @param {string | null} guestToken
   */
  SET_GUEST_TOKEN(state, guestToken) {
    state.guestToken = guestToken
  },

  /**
   * @param {CartState} state
   * @param {{
   *   itemId: string,
   *   itemSet: Partial<CartItem>,
   * }} payload
   */
  UPDATE_ITEM(state, { itemId, itemSet }) {
    const item = state.items.find((_item) => _item.item_id === itemId)
    if (!item) return
    // `quantity` is reserved to SET_ITEM_QTY
    // `is_server_sync: true` is reserved to SET_ITEM_SYNC
    const { quantity, is_server_sync: isServerSync, ...restToSet } = itemSet
    if (isServerSync === false) {
      item.is_server_sync = false
    }
    Object.keys(restToSet).forEach((key) => {
      Vue.set(item, key, restToSet[key])
    })
  },

  /**
   * @param {CartState} state
   * @param {{
   *   itemId: string,
   *   quantity: number,
   *   isSkipServerSync?: boolean,
   * }} payload
   */
  SET_ITEM_QTY(state, { itemId, quantity, isSkipServerSync }) {
    quantity = Number(quantity)
    if (isNaN(quantity) || quantity < 0) return
    const itemIndex = state.items.findIndex((_item) => _item.item_id === itemId)
    if (itemIndex > -1) {
      const item = state.items[itemIndex]
      item.quantity = quantity
      if (!isSkipServerSync) {
        item.is_server_sync = false
      } else if (!item.quantity) {
        dropCartItem(state.items, itemIndex)
      }
    }
  },

  /**
   * @param {CartState} state
   * @param {string} itemId
   */
  SET_ITEM_SYNC(state, itemId) {
    const itemIndex = state.items.findIndex((_item) => _item.item_id === itemId)
    if (itemIndex > -1) {
      const item = state.items[itemIndex]
      item.is_server_sync = true
      if (!item.quantity) {
        dropCartItem(state.items, itemIndex)
      }
    }
  },

  /**
   * @param {CartState} state
   * @param {string | null} zipCode
   */
  SET_ZIP_CODE(state, zipCode) {
    state.zipCode = zipCode
  },

  /**
   * @param {CartState} state
   * @param {boolean} isServerSyncing
   */
  SET_CART_SYNCING(state, isServerSyncing) {
    state.isServerSyncing = isServerSyncing
  },
}

/**
 * @typedef {{
 *   state: CartState,
 *   getters: Record<keyof getters, any>,
 *   commit: (name: keyof mutations, payload: any) => any,
 *   dispatch: (name: keyof actions, payload: any) => any,
 *   rootState: Record<string, any>,
 *   rootGetters: Record<string, any>,
 * }} ActionContext
 */

/**
 * @param {ActionContext} context
 */
const getOrderInfo = ({ state, rootState, rootGetters }) => {
  /** @type {CartState['userId']} */
  const userId = rootState.user?.id || state.userId
  /** @type {CartState['orderNumber']} */
  const orderNumber = rootState.orderNumber || state.orderNumber
  const { guestToken } = state
  const { storeInfo } = rootGetters
  return {
    userId,
    orderNumber,
    guestToken,
    storeInfo,
  }
}

/** @type {Record<string, Promise<any>>} */
const syncingByItemId = {}
/**
 * @typedef {{
 *   ctx: ActionContext,
 *   axios: any,
 *   itemId: string,
 *   quantity?: number,
 * }} ItemToServerOpt
 */
/** @type {(debounceId: string, opt: ItemToServerOpt) => void} */
const debouncedItemToServer = debounce(
  /** @type {(opt: ItemToServerOpt) => Promise<any>} */
  async ({ ctx, axios, itemId, quantity }) => {
    if (syncingByItemId[itemId]) {
      await syncingByItemId[itemId]
    }
    const { userId, orderNumber, storeInfo } = getOrderInfo(ctx)
    if (userId !== ctx.state.userId) {
      // This is a double check and not expected, debug?
      ctx.dispatch('setCartInfo', { userId })
    }
    if (orderNumber !== ctx.state.orderNumber) {
      ctx.dispatch('setCartInfo', { orderNumber })
    }
    const cartItem = ctx.state.items.find((_item) => _item.item_id === itemId)
    if (!cartItem) return
    if (cartItem.is_out_of_stock && cartItem.quantity) return
    if (cartItem.is_gift && !cartItem.line_item_id) {
      // Cannot create gift item, only update with ID
      return
    }
    /** @type {Function | undefined} */
    let resolveSyncingById
    syncingByItemId[itemId] = new Promise((resolve) => {
      resolveSyncingById = resolve
    })
    /** @type {Partial<CartItem>} */
    const itemSet = {}
    /** @type {Record<string, any> | undefined} */
    let serverCart
    try {
      /** @type {Record<string, any> | undefined} */
      let data
      if (cartItem.is_gift) {
        data = await axios.$post(
          appendUrlParam('carts/update_gift', {
            ...storeInfo,
            line_item_id: cartItem.line_item_id,
            variant_id: cartItem.variant_id,
          })
        )
      } else if (cartItem.line_item_id) {
        data = await axios.$post(
          '/carts/update_contents',
          {
            order: {
              line_items_attributes: [
                {
                  id: cartItem.line_item_id,
                  quantity: cartItem.quantity,
                },
              ],
            },
          },
          {
            params: { ...storeInfo },
          }
        )
      } else {
        data = await axios.$post('/carts/populate', {
          ...storeInfo,
          variant_id: cartItem.variant_id,
          quantity: quantity || cartItem.quantity,
          customizations: cartItem.customizations,
          image_url: cartItem.image_url,
          engine_information: cartItem.engine_information,
          device_id: cartItem.device?.id || cartItem.device_id,
          case_device_id: cartItem.case_device_id,
          added_by_suggestion: cartItem.added_by_suggestion,
          number: orderNumber,
          user_id: userId,
        })
      }
      if (data) {
        if (
          data.order_number &&
          ctx.rootState.orderNumber !== data.order_number
        ) {
          ctx.commit('SET_ORDER_NUMBER', data.order_number, { root: true })
        }
        if (data.order_quantity) {
          ctx.commit('SET_CART_QTY', data.order_quantity, { root: true })
        }
        if (data.guest_token) {
          ctx.dispatch('setCartInfo', { guestToken: data.guest_token })
        }
        if (data.line_item_id) {
          itemSet.line_item_id = data.line_item_id
        }
        // @TODO: Possible to get image from `/carts/populate` response ?
        serverCart = data.cart
      }
      itemSet.is_server_sync = true
    } catch (err) {
      if (!cartItem.quantity) {
        itemSet.is_server_sync = true
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to sync cart item:', err)
        itemSet.is_server_sync = false
      }
    }
    ctx.dispatch('updateItem', { itemId, itemSet })
    if (resolveSyncingById) {
      resolveSyncingById(null)
    }
    if (serverCart) {
      ctx.dispatch('syncWithServer', serverCart)
    } else if (
      cartItem.raw_customizations &&
      Object.keys(cartItem.raw_customizations).length &&
      !cartItem.case_device
    ) {
      // Fetch cart to get customized item image faster
      setTimeout(() => {
        if (ctx.state.isServerSyncing) return
        ctx.dispatch('syncWithServer')
      }, 600)
    } else {
      setTimeout(() => {
        if (ctx.state.isServerSyncing) return
        if (ctx.getters.isServerSync && ctx.getters.isGiftsMatching) return
        ctx.dispatch('syncWithServer')
      }, 600)
    }
  },
  500,
  { withId: true }
)

/**
 * @typedef {{
 *   ctx: ActionContext,
 *   axios: any,
 *   cart?: Record<string, any>,
 * }} SyncWithServerOpt
 */
/** @type {(opt: SyncWithServerOpt) => void} */
const debouncedSyncWithServer = debounce(
  /** @type {(opt: SyncWithServerOpt) => Promise<any>} */
  async ({ ctx, axios, cart }) => {
    const { state, getters, commit, dispatch } = ctx
    const { orderNumber, guestToken, storeInfo } = getOrderInfo(ctx)
    const params = { ...storeInfo, number: orderNumber }
    if (guestToken) {
      params.guest_token = guestToken
    }
    ctx.commit('SET_CART_SYNCING', true)
    if (!cart) {
      cart = (await axios.$get(`/cart`, { params })).cart
    }
    /** @type {Array<CartItem & { id: number }>} */
    const serverItems = cart?.line_items
    if (!Array.isArray(serverItems)) return
    serverItems.forEach(async (serverItem) => {
      const isToRemove = serverItem.discontinued_case_device === true
      /** @type {Partial<CartItem>} */
      const itemSet = {}
      const lineItemId = serverItem.id
      let localItem = state.items.find((_item) => {
        return Number(_item.line_item_id) === lineItemId
      })
      const serverItemId = generateCartItemId(
        serverItem,
        serverItem.customizations
      )
      if (!localItem) {
        localItem = state.items.find((_localItem) => {
          if (_localItem.line_item_id) return false
          if (_localItem.is_gift && _localItem.gift_for_item_id) {
            const localGiftParent = state.items.find((_item) => {
              return _item.item_id === _localItem.gift_for_item_id
            })
            const parentItem = findEligibleGift(
              localGiftParent,
              serverItem.variant_id
            )
            return Boolean(parentItem)
          }
          const normalizedItemId = generateCartItemId(
            _localItem,
            _localItem.server_customizations || _localItem.customizations
          )
          return normalizedItemId === serverItemId
        })
        if (!localItem && !isToRemove) {
          if (!(serverItem.quantity > 0) || !serverItem.in_stock) return
          /** @type CartItem */
          const newItem = {
            ...serverItem,
            line_item_id: lineItemId,
            is_server_sync: true,
          }
          if (newItem.gift || newItem.is_gift) {
            // @TODO: Get gift parent identification from server
            /** @type {CartItem} */
            const parentItem = getters.activeItems.find((_item) => {
              return Boolean(findEligibleGift(_item, newItem.variant_id))
            })
            if (parentItem) newItem.gift_for_item_id = parentItem.item_id
          }
          dispatch('addItem', {
            productOrItem: newItem,
            canSendToServer: false,
          })
          return
        }
        if (localItem) {
          itemSet.line_item_id = lineItemId
        }
      }
      if (!localItem) return
      const itemId = localItem.item_id
      if (syncingByItemId[itemId]) {
        await syncingByItemId[itemId]
      }
      if (isToRemove) {
        itemSet.quantity = 0
        itemSet.is_server_sync = true
      } else {
        if (localItem.quantity === serverItem.quantity) {
          if (!localItem.is_server_sync) {
            itemSet.is_server_sync = true
          }
        } else if (localItem.is_server_sync) {
          itemSet.is_server_sync = false
        }
        // We shouldn't overwrite gifts info here because it may switch locally (choosable)
        if (!serverItem.gift) {
          /** @type {Array<keyof CartItem>} */
          const numberFields = ['price', 'old_price']
          numberFields.forEach((field) => {
            if (serverItem[field]) itemSet[field] = Number(serverItem[field])
          })
          /** @type {Array<keyof CartItem>} */
          const fieldsToPull = [
            'case_device',
            'case_device_id',
            'device_id', // -> device.id
            'image_url',
            'name',
            'old_price',
            'price',
            'product_id',
            'slug',
            'variant_id',
          ]
          fieldsToPull.forEach((field) => {
            if (!serverItem[field]) return
            if (field.endsWith('price')) {
              itemSet[field] = Number(serverItem[field])
              return
            }
            itemSet[field] = serverItem[field]
          })
          if (!itemSet.image_url && serverItem.image) {
            itemSet.image_url = serverItem.image
          }
          const isOutOfStock = !serverItem.in_stock
          if (isOutOfStock !== Boolean(itemSet.is_out_of_stock)) {
            itemSet.is_out_of_stock = isOutOfStock
          }
        } else {
          // @TODO: Granular way to check gift items added without `gift_for_item_id`
          // eslint-disable-next-line no-lonely-if
          if (Number(serverItem.variant_id) !== Number(localItem.variant_id)) {
            itemSet.is_server_sync = false
          }
        }
        if (serverItem.customizations) {
          itemSet.server_customizations = serverItem.customizations
        }
      }
      if (Object.keys(itemSet).length) {
        dispatch('updateItem', {
          itemId,
          itemSet,
          canSendToServer: itemSet.is_server_sync === false,
        })
      }
    })
    const giftsToDelete = state.items.filter((localItem) => {
      if (!localItem.is_gift) return false
      return !serverItems.find((serverItem) => {
        return Number(localItem.line_item_id) === serverItem.id
      })
    })
    for (const giftItem of giftsToDelete) {
      const itemId = giftItem?.item_id
      if (!itemId) continue
      commit('SET_ITEM_QTY', { itemId, quantity: 0, isSkipServerSync: true })
    }
    state.items.forEach((localItem) => {
      if (!localItem.is_gift) return false
      dispatch('fixItemGifts', { itemId: localItem.item_id })
    })
    /** @type {CartItem[]} */
    const itemsToSync = getters.itemsNeedingSync
    for (const localItem of itemsToSync) {
      const itemId = localItem?.item_id
      if (!itemId) continue
      await dispatch('sendItemToServer', { itemId })
    }
    ctx.commit('SET_CART_SYNCING', false)
  },
  1000,
  { immediate: true }
)

/**
 * @typedef {{
 *   ctx: ActionContext,
 *   itemId: string,
 * }} FixItemGiftsOpts
 */
/** @type {(debounceId: string, opt: FixItemGiftsOpts) => void} */
const debouncedFixItemGifts = debounce(
  /** @type {(opt: FixItemGiftsOpts) => Promise<any>} */
  async ({ ctx, itemId }) => {
    /** @type {CartItem | undefined} */
    const item = ctx.state.items.find((_item) => _item.item_id === itemId)
    if (!item || item.is_gift) return
    const currentGifts = ctx.state.items.filter(
      (_item) => _item.is_gift && _item.gift_for_item_id === itemId
    )
    if (
      !item.eligible_gifts?.length ||
      !item.quantity ||
      item.is_out_of_stock
    ) {
      for (const giftItem of currentGifts) {
        await ctx.dispatch('setItemQty', {
          itemId: giftItem.item_id,
          quantity: 0,
          canSendToServer: false,
        })
      }
      return
    }
    const currentGiftsQty = currentGifts.reduce(
      (acc, gift) => acc + gift.quantity,
      0
    )
    if (currentGiftsQty === item.quantity) return
    if (currentGiftsQty > item.quantity) {
      let qtyToRemove = currentGiftsQty - item.quantity
      for (const gift of currentGifts) {
        if (qtyToRemove <= 0) break
        const qtyToRemoveFromThisGift = Math.min(gift.quantity, qtyToRemove)
        const newQuantity = gift.quantity - qtyToRemoveFromThisGift
        await ctx.dispatch('setItemQty', {
          itemId: gift.item_id,
          quantity: newQuantity,
          canSendToServer: false,
        })
        qtyToRemove -= qtyToRemoveFromThisGift
      }
      return
    }
    /** @type {Partial<CartItem> | undefined} */
    let gift
    const maybeItemColor = item.case_type?.name?.toLowerCase()
    if (maybeItemColor) {
      gift = item.eligible_gifts.find((gift) => {
        if (!gift.in_stock) return false
        return gift.name?.toLowerCase().includes(maybeItemColor)
      })
    }
    if (!gift) {
      gift = item.eligible_gifts.find((gift) => gift.in_stock)
    }
    if (!gift) return
    gift.is_gift = true
    gift.gift_for_item_id = itemId
    const quantity = item.quantity - currentGiftsQty
    for (let i = 0; i < quantity; i++) {
      // For gifts is better to create a line item each unit
      await ctx.dispatch('addItem', {
        productOrItem: gift,
        canSendToServer: false,
      })
    }
  },
  150,
  { immediate: true, withId: true }
)

export const actions = {
  /* eslint-disable require-await */
  /**
   * @param {ActionContext} context
   * @param {Partial<Omit<CartState, 'items'>>} cartInfo
   */
  setCartInfo({ state, commit }, { userId, orderNumber, guestToken, zipCode }) {
    if (userId !== undefined) {
      if (state.userId && state.orderNumber && state.userId !== userId) {
        commit('RESET_CART')
      }
      commit('SET_USER_ID', userId)
    }
    if (orderNumber !== undefined) {
      if (
        state.orderNumber &&
        orderNumber &&
        state.orderNumber !== orderNumber
      ) {
        commit('RESET_CART')
      }
      if (!state.orderNumber || orderNumber) {
        commit('SET_ORDER_NUMBER', orderNumber)
      }
    }
    if (guestToken) {
      commit('SET_GUEST_TOKEN', guestToken)
    }
    if (zipCode) {
      commit('SET_ZIP_CODE', zipCode)
    }
  },

  /**
   * @param {ActionContext} context
   * @param {{
   *   productOrItem: Record<string, any> & CartItem,
   *   canSendToServer?: boolean,
   * }} payload
   */
  async addItem(
    { commit, dispatch },
    { productOrItem, canSendToServer = true }
  ) {
    /** @type {CartItem} */
    let cartItem
    try {
      cartItem = normalizeItem(productOrItem)
      commit('ADD_ITEM', cartItem)
    } catch (err) {
      console.error(err)
      throw err
    }
    if (!canSendToServer) return
    const { item_id: itemId, quantity } = cartItem
    await dispatch('sendItemToServer', { itemId, quantity })
  },

  /**
   * @param {ActionContext} context
   * @param {{
   *   itemId: string,
   *   quantity: number,
   *   canSendToServer?: boolean,
   * }} payload
   */
  async setItemQty(
    { commit, dispatch },
    { itemId, quantity, canSendToServer = true }
  ) {
    commit('SET_ITEM_QTY', {
      itemId,
      quantity,
      isSkipServerSync: canSendToServer === false,
    })
    await dispatch('fixItemGifts', { itemId })
    if (!canSendToServer) return
    await dispatch('sendItemToServer', { itemId, quantity })
  },

  /**
   * @param {ActionContext} context
   * @param {{ itemId: string, quantity?: number }} payload
   */
  async sendItemToServer(ctx, { itemId, quantity }) {
    const axios = this.$axios
    debouncedItemToServer(itemId, { ctx, axios, itemId, quantity })
  },

  /**
   * @param {ActionContext} context
   * @param {{
   *   itemId: string,
   *   itemSet: Partial<CartItem>,
   *   canSendToServer?: boolean,
   * }} payload
   */
  async updateItem(
    { state, commit, dispatch },
    { itemId, itemSet, canSendToServer = true }
  ) {
    const item = state.items.find((_item) => _item.item_id === itemId)
    if (item) {
      const { quantity, is_server_sync: isServerSync } = itemSet
      commit('UPDATE_ITEM', { itemId, itemSet })
      if (quantity >= 0) {
        await dispatch('setItemQty', {
          itemId,
          quantity,
          canSendToServer: canSendToServer && isServerSync !== true,
        })
      }
      if (item.is_server_sync || isServerSync) {
        commit('SET_ITEM_SYNC', itemId)
      }
      if (item.is_gift && !item.is_server_sync) {
        // Should update gift variant ID as long as line item ID is present
        await dispatch('sendItemToServer', { itemId, quantity })
      }
    }
  },

  /**
   * @param {ActionContext} ctx
   * @param {{
   *   itemId: string
   * }} payload
   */
  async fixItemGifts(ctx, { itemId }) {
    debouncedFixItemGifts(itemId, { ctx, itemId })
  },

  /**
   * @param {ActionContext} context
   * @param {{
   *   itemId: string,
   *   gifts: CartItem['eligible_gifts'],
   * }} payload
   */
  async setItemEligibleGifts({ state, commit, dispatch }, { itemId, gifts }) {
    const item = state.items.find((_item) => _item.item_id === itemId)
    if (!item) return
    /** @type Partial<CartItem> */
    const itemSet = { eligible_gifts: gifts }
    commit('UPDATE_ITEM', { itemId, itemSet })
    await dispatch('fixItemGifts', { itemId })
  },

  /**
   * @param {ActionContext} context
   * @param {{
   *   itemId: string,
   *   gift: CartItem['eligible_gifts'][0],
   * }} payload
   */
  async changeGiftVariant({ state, dispatch }, { itemId, gift }) {
    if (!gift?.variant_id || gift.in_stock === false) return
    const item = state.items.find((_item) => _item.item_id === itemId)
    if (!item?.is_gift) return
    /** @type Partial<CartItem> */
    const itemSet = {
      variant_id: gift.variant_id,
      name: gift.name,
      image_url: gift.image,
      is_server_sync: false,
    }
    await dispatch('updateItem', { itemId, itemSet })
  },

  /**
   * @param {ActionContext} ctx
   * @param {Record<string, any> | undefined} cart
   */
  async syncWithServer(ctx, cart) {
    debouncedSyncWithServer({ ctx, axios: this.$axios, cart })
  },
}
